import { Button, CircularProgress, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";
import { Download } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { heicTo } from "heic-to";
import api from "../../api";
import { useLoading } from "../../hooks/loading";
import { toast } from "react-toastify";
import UploadButton from "../../components/UploadButton";

export const FileViewer = ({
  enrollmentId,
  selectedFile,
  setSelectedFile,
  selectedFileIndex,
  setSelectedFileIndex,
  files,
  setFiles,
  onFilesDropped,
}) => {
  const classes = useStyles();
  const [convertedUrl, setConvertedUrl] = useState(null);
  const [converting, setConverting] = useState(false);

  const { startLoading, stopLoading } = useLoading();

  const isHeic = (file) => {
    if (!file) return false;
    const mime = file.mimeType?.toLowerCase() || "";
    const name = file.name?.toLowerCase() || "";
    return mime === "image/heic" || mime === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
  };

  useEffect(() => {
    if (convertedUrl && convertedUrl.startsWith("blob:")) {
      URL.revokeObjectURL(convertedUrl);
    }
    setConvertedUrl(null);

    if (!selectedFile || !isHeic(selectedFile)) return;

    let cancelled = false;
    const xhr = new XMLHttpRequest();
    setConverting(true);

    xhr.open("GET", selectedFile.url, true);
    xhr.responseType = "blob";
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        handleError(new Error(`HTTP ${xhr.status}`));
        return;
      }
      heicTo({ blob: xhr.response, type: "image/jpeg", quality: 0.85 })
        .then((jpegBlob) => {
          if (!cancelled) setConvertedUrl(URL.createObjectURL(jpegBlob));
        })
        .catch(handleError)
        .finally(() => setConverting(false));
    };
    xhr.onerror = () => handleError(new Error("Network error"));
    xhr.send();

    function handleError(err) {
      console.warn("HEIC conversion failed, falling back to direct render:", err);
      if (!cancelled) setConvertedUrl(selectedFile.url);
      setConverting(false);
    }

    return () => {
      cancelled = true;
      xhr.abort();
    };
  }, [selectedFile]);

  useEffect(() => {
    if (enrollmentId) {
      (async () => {
        startLoading();
        try {
          const result = await api.getFilesForEnrollment(enrollmentId);
          setFiles(result.data.files);
        } catch (err) {
          toast.error(err.message);
        }
        stopLoading();
      })();
    }
  }, [enrollmentId]);

  useEffect(() => {
    if (files) {
      setSelectedFile(files[selectedFileIndex]);
    }
  }, [files, selectedFileIndex]);

  if (!files || files.length === 0) {
    return <Typography>No uploaded documents</Typography>;
  }

  return (
    <div>
      <div className={classes.headerContainer}>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={selectedFileIndex}
          style={{ maxWidth: "70%" }}
          onChange={(e) => {
            setSelectedFileIndex(e.target.value);
          }}
        >
          {files?.map((file, index) => (
            <MenuItem key={file.id} value={index}>
              {file.name}
            </MenuItem>
          ))}
        </Select>
        <Button
          href={selectedFile?.url}
          target="_blank"
          download={true}
          className={classes.downloadButton}
          variant="outlined"
          color="primary"
        >
          <Download />
        </Button>

        <UploadButton onFilesDropped={onFilesDropped} />
      </div>

      {selectedFile && (
        <>
          {(selectedFile.mimeType === "application/pdf" ||
            selectedFile.name.endsWith("pdf")) && (
            <iframe
              style={{ width: "100%", height: "75vh", marginTop: 32 }}
              src={selectedFile.url}
              title={selectedFile.name}
            ></iframe>
          )}
          {selectedFile.mimeType.startsWith("image") && (
            converting ? (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
                <CircularProgress />
              </div>
            ) : (
              <img
                key={selectedFile.url}
                style={{ width: "100%", marginTop: 32 }}
                src={isHeic(selectedFile) ? convertedUrl : selectedFile.url}
                alt={selectedFile.name}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  headerContainer: {
    display: "flex",
    flexDirection: "row",
  },
  downloadButton: {
    marginLeft: 16,
  },
}));
